import { useEffect, useRef } from 'react';

const FOCUSABLE_FIELD_SELECTOR =
	'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])';

/**
 * Focuses the first enabled field inside a container while it is active —
 * including mounting already-active (e.g. a slot revealed straight into its open
 * form). Callers gate `active` so this only fires on an intentional open/add,
 * never on passive load. Returns a ref to attach to the container.
 *
 * Focus is deliberately driven off `active` being true (not a false→true edge):
 * under StrictMode the effect is set up, torn down, then set up again, and an
 * edge-based version would cancel its scheduled focus on teardown and then see
 * no edge on the re-run. `preventScroll` keeps the focus from fighting a smooth
 * scroll-into-view, the `contains(activeElement)` guard avoids stealing focus
 * the user has already placed inside, and `onOpen` runs on open (e.g. to reset a
 * form's state).
 */
export const useAutoFocusFirstField = <T extends HTMLElement = HTMLElement>(active: boolean, onOpen?: () => void) => {
	const containerRef = useRef<T>(null);
	const onOpenRef = useRef(onOpen);
	onOpenRef.current = onOpen;

	useEffect(() => {
		if (!active) return;

		onOpenRef.current?.();
		const frame = requestAnimationFrame(() => {
			const container = containerRef.current;
			if (!container || container.contains(document.activeElement)) return;
			container.querySelector<HTMLElement>(FOCUSABLE_FIELD_SELECTOR)?.focus({ preventScroll: true });
		});
		return () => cancelAnimationFrame(frame);
	}, [active]);

	return containerRef;
};
